import { observer } from 'mobx-react';
import React from 'react';
import {
  AutoSizer,
  Column,
  ColumnProps,
  SortDirectionType,
  Table,
  TableHeaderProps,
  WindowScroller,
} from 'react-virtualized';
import { sortBy, SortOrder } from '../../util/sort';
import { ScrollableContainerContext } from '../appContainer';
import { Glyphicon } from '../icons';

interface VirtualizedColumnProps<T> extends ColumnProps {
  dataKey: keyof T | string;
  key?: string;
}

interface VirtualizedDataTableProps<T> {
  columnProps: Array<VirtualizedColumnProps<T>>;
  data: Array<T>;
  groupByKey?: keyof T;
  initialSortKey?: keyof T;
  initialSortDirection?: SortDirectionType;
}

interface VirtualizedDataTableState<T> {
  sortByKey: keyof T;
  sortDirection: SortDirectionType;
}

@observer
export class VirtualizedDataTable<T> extends React.Component<
  VirtualizedDataTableProps<T>,
  VirtualizedDataTableState<T>
> {
  static contextType = ScrollableContainerContext;

  constructor(props: Readonly<VirtualizedDataTableProps<T>>) {
    super(props);

    const { initialSortKey, initialSortDirection } = this.props;

    this.state = {
      sortByKey: initialSortKey,
      sortDirection: initialSortDirection,
    };
  }

  handleSort(info: { sortBy: string; sortDirection: SortDirectionType }) {
    const data = this.props.data;

    if (!data || !Object.keys(data[0]).some(x => x == info.sortBy)) {
      console.warn('SortBy key not found in data.');
    }

    this.setState({
      sortByKey: info.sortBy as keyof T,
      sortDirection: info.sortDirection,
    });
  }

  render() {
    const { columnProps, data, groupByKey } = this.props;

    const { sortByKey, sortDirection } = this.state;

    let sortOrder = sortDirection == 'ASC' ? 'asc' : ('desc' as SortOrder);

    const sortedData = sortBy(data, sortByKey, sortOrder, groupByKey);

    return (
      <WindowScroller scrollElement={this.context.current}>
        {scrollerProps => (
          <AutoSizer disableHeight>
            {sizerProps => (
              <Table
                gridStyle={{ outlineWidth: 0 }}
                headerHeight={35}
                autoHeight
                height={scrollerProps.height || 500}
                rowCount={sortedData.length}
                rowHeight={35}
                width={sizerProps.width}
                rowGetter={({ index }) => sortedData[index]}
                sort={info => this.handleSort(info)}
                sortBy={String(sortByKey)}
                sortDirection={sortDirection}
                noRowsRenderer={() => <div>No data.</div>}
                scrollLeft={scrollerProps.scrollLeft}
                scrollTop={scrollerProps.scrollTop}
                isScrolling={scrollerProps.isScrolling}
                onScroll={scrollerProps.onChildScroll}
              >
                {columnProps.map(col => {
                  const { headerStyle, key, dataKey, ...rest } = col;

                  return (
                    <Column
                      key={key || String(col.dataKey)}
                      dataKey={String(dataKey)}
                      headerStyle={{
                        textTransform: 'none',
                        outlineWidth: 0,
                        userSelect: 'none',
                        ...headerStyle,
                      }}
                      headerRenderer={e => this.renderTableHeaderCell(e)}
                      {...rest}
                    />
                  );
                })}
              </Table>
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    );
  }

  renderTableHeaderCell(cellProps: TableHeaderProps) {
    const { dataKey, label, disableSort, sortBy, sortDirection } = cellProps;

    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        {label || ''}
        {!disableSort &&
          dataKey == sortBy && (
            <Glyphicon
              style={{ verticalAlign: 'middle' }}
              glyph={sortDirection === 'ASC' ? 'arrowUp' : 'arrowDown'}
            />
          )}
      </div>
    );
  }
}
